'use client';

import { Box, Button } from '@chakra-ui/react';
import type React from 'react';
import { MdOutlineChat } from 'react-icons/md';

import { useI18n } from '@/__generated__/i18n.next';
import { useChatPaneSelector } from '@/core/components/ChatPaneContext';
import { useChatAvailability } from '@/core/components/useChatAvailability';

export const ChatPaneOpenButton: React.FC = () => {
  const t = useI18n();
  const updateChatPane = useChatPaneSelector(([_, update]) => update);
  const { isVisible } = useChatPaneButtonVisibility();

  return (
    <Button
      id="chat-pane-open-button"
      display={isVisible ? 'inline-flex' : 'none'}
      bottom={4}
      colorPalette="brand"
      pos="fixed"
      right={4}
      shadow="lg"
      onClick={() => {
        updateChatPane((draft) => {
          draft.isOpen = true;
        });
      }}
    >
      <MdOutlineChat />
      {t.components.chatPane.title()}
    </Button>
  );
};

/**
 * Reserves room below content that can extend under the fixed button, e.g. the problem editor once the user collapses
 * the right pane, so it is not gated by viewport width.
 */
export const ChatPaneOpenButtonSpacer: React.FC = () => {
  const { shouldReserveSpace } = useChatPaneButtonVisibility();
  if (!shouldReserveSpace) return <></>;

  return <Box flexShrink={0} h={16} />;
};

function useChatPaneButtonVisibility(): { isVisible: boolean; shouldReserveSpace: boolean } {
  const isEmbeddedMode = useChatPaneSelector(([value]) => value.isEmbeddedMode);
  const isOpen = useChatPaneSelector(([value]) => value.isOpen);
  const { hasScope, isChatDisabled } = useChatAvailability();
  // Loading and failed availability count as unavailable so the button never flashes on pages without chat.
  const shouldReserveSpace = !isEmbeddedMode && hasScope && isChatDisabled === false;
  return { isVisible: shouldReserveSpace && !isOpen, shouldReserveSpace };
}
